import React from 'react';

import useWindowDimensions from './use-window-dimensions';
import { _ } from '../libs';

interface UseDataGridContainerDimensionsOptions {
  setTableWidth: (_width: number) => void;
  tableWidth?: number;
  customHeight?: number;
  triggerWidthChange?: boolean;
}

interface UseDataGridContainerDimensionsReturn {
  containerRef: React.RefObject<HTMLDivElement>;
  containerHeight: number | undefined;
  dataGridHeight: number;
  tableHeight: number;
}

/**
 * Hook that manages container dimensions for DataGrid components.
 * Handles container height tracking and table width updates with proper cleanup
 * to prevent infinite loops and null reference errors.
 *
 * @param options - Configuration options
 * @param options.setTableWidth - Function to update the table width
 * @param options.tableWidth - Current table width value
 * @param options.customHeight - Optional custom height override
 * @param options.triggerWidthChange - Optional trigger to force width recalculation
 * @returns Object containing container ref, height state, and calculated dimensions
 */
const useDataGridContainerDimensions = ({
  setTableWidth,
  tableWidth,
  customHeight,
  triggerWidthChange,
}: UseDataGridContainerDimensionsOptions): UseDataGridContainerDimensionsReturn => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = React.useState<number>(undefined);

  const { width, height } = useWindowDimensions(0);

  const debouncedSetTableWidth = React.useMemo(
    () =>
      _.debounce(() => {
        if (containerRef.current) {
          const newWidth = containerRef.current.offsetWidth;
          setTableWidth(newWidth);
        }
      }, 300),
    [setTableWidth]
  );

  React.useEffect(() => {
    if (!containerRef.current) return;

    const newHeight = containerRef.current.offsetHeight;
    const newWidth = containerRef.current.offsetWidth;

    // Use Math.floor to prevent floating point precision issues causing loops
    const roundedNewHeight = Math.floor(newHeight);
    const currentRoundedHeight = containerHeight !== undefined ? Math.floor(containerHeight) : undefined;

    if (currentRoundedHeight === undefined || currentRoundedHeight !== roundedNewHeight) {
      setContainerHeight(newHeight);
    }

    if (tableWidth === undefined || Math.floor(tableWidth) !== Math.floor(newWidth)) {
      debouncedSetTableWidth();
    }

    return () => {
      debouncedSetTableWidth.cancel();
    };
  }, [width, height, containerHeight, debouncedSetTableWidth, tableWidth, triggerWidthChange]);

  const dataGridHeight = customHeight ? customHeight : containerHeight ? containerHeight : 300;
  const tableHeight = dataGridHeight - 10;

  return {
    containerRef,
    containerHeight,
    dataGridHeight,
    tableHeight,
  };
};

export default useDataGridContainerDimensions;
